import React from "react";

import { ProgressBar } from "@blueprintjs/core";
import { INTENT_SUCCESS } from "@blueprintjs/core/lib/esm/common/classes";
import { CourseWrapper } from "common/entities/courseWrapper/CourseWrapper";
import { FlexJustify } from "common/fragments/Flex";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardVariant, CardView } from "common/views/card/Card.view";
import { DropdownSelectVariant } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import ProgressBarView from "common/views/progressBar/ProgressBar.view";
import { Query } from "core/api/typedQuery";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { isEmpty } from "core/util/ArrayUtils";
import { validateList } from "core/util/ParamUtil";
import StringUtil from "core/util/StringUtil";
import { iDef, iDefL, nDef } from "core/util/TypeUtils";
import labelProvider from './BundleEntryList.labels';
import { Config } from "./BundleEntryListConfig";
import { Entry } from "./entities/Entry";
import { Organization } from "./entities/OrganizationEntries";


const { Labels } = labelProvider;
/**
 * @description panel that displays courses available to an organization through associated bundles.
 * @author chills
 */

interface IOrganizationParams {
	guid: string;
	pageSize?: number;
	sort?: string;
	query?: string;
}

const OrganizationQuery = Query.queryOne<Organization, IOrganizationParams>(Organization);

const PARAM_ORG_GUID = "guid";
const PARAM_ORG_ENTRIES_PAGE_SIZE = "org.entries.pageSize";
const PARAM_ORG_ENTRIES_SORT = "org.entries.sort";
const PARAM_ORG_ENTRIES_QUERY = "org.entries.query";

const sortOptions = [
	{value: "startDate", label: "Recently Released"},
	{value: "item.name", label: "Name"}
];

@Panel({id: "organization-entry-list", configClass: Config, pageParams: true, labelProvider})
export class OrganizationEntryListPanel extends React.Component<IPanelProps<Config>> {

	public render() {
		const { config, pageParams } = this.props;

		const guid = pageParams[PARAM_ORG_GUID];

		if (nDef(guid)) {
			return (
				<NonIdealState
					title={<Labels.OrganizationEmpty />}
					description={<Labels.DescriptionEmpty />}
				/>
			);
		}

		const defaultPageSize = iDef(config.pageSize) ? config.pageSize : 10;
		const pageSize = StringUtil.toInteger(pageParams[PARAM_ORG_ENTRIES_PAGE_SIZE], defaultPageSize);

		const params: IOrganizationParams = {
			guid,
			pageSize,
			sort: validateList(['item.name', 'startDate', 'endDate'], pageParams[PARAM_ORG_ENTRIES_SORT], null),
			query: pageParams[PARAM_ORG_ENTRIES_QUERY] || null
		};

		return [
			config.enableRefine && (
				<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
					<FlexJustify>
						<OptionDropdownSelect
							options={sortOptions}
							value={pageParams[PARAM_ORG_ENTRIES_SORT]}
							onChange={this.onChangeParam(PARAM_ORG_ENTRIES_SORT)}
							variant={DropdownSelectVariant.MINIMAL}
							viewKey="refine.sort"
						>
							<Labels.Sort />
						</OptionDropdownSelect>


					</FlexJustify>
				</BlockView>
			),
			(
				<OrganizationQuery key="query" params={params}>
					{({data, loading}) => {
						if (loading) {
							return <Loading key="loading" />;
						}

						if (!data) {
							return (
								<NonIdealState key="notfound" title="Invalid Organization" description="Unable to locate selected organization." />
							);
						}

						if (isEmpty(data.entryList)) {
							return (
								<NonIdealState key="empty" title="No Courses Found" description="Try different search parameters!" />
							);
						}

						return (
							<div key="data">
								{data.entryList.map(this.renderEntry)}
								{
									data.entryList.length >= pageSize && (
										<BlockView variant={BlockVariant.PAGING} key="paging" viewKey="paging">
											<ButtonView
												onClick={() => this.onChangeParam(PARAM_ORG_ENTRIES_PAGE_SIZE)(pageSize + defaultPageSize)}
												variant={ButtonVariant.PRIMARY}
												viewKey="load-more"
											>
												<Labels.LoadMore />
											</ButtonView>
										</BlockView>
									)
								}
							</div>
						);
					}}
				</OrganizationQuery>
			)
		];
	}

	private renderEntry = (entry: Entry) => {
		const w = CourseWrapper.forEntry(entry);
		return (
			<CardView variant={CardVariant.SURFACE} viewKey="card">
				<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="card-content">
					<FlexJustify>
						<div>
							<HeadingView viewKey="title" children={entry.displayName || entry.product.name} />
							<ProgressBar animate={false} intent={'success'} stripes={false}>
								<ProgressBarView viewKey="course-progress" value={w.getProgressPercent()}></ProgressBarView>
							</ProgressBar>
						</div>
						<div>

						</div>
					</FlexJustify>
				</BlockView>
			</CardView>
		);
	}

	private onChangeParam = (name: string) => (value: any) => {
		if (iDefL(value)) {
			this.props.actions.pushPageParam(name, value);
		} else {
			this.props.actions.removePageParam(name);
		}
	}
}



// WEBPACK FOOTER //
// ./src/panels/organizationAdmin/BundleEntryList.panel.tsx